import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { PrimaryButton, SecondaryButton } from '../../../../components/Button';
import EmptyState from '../../../../components/Card/EmptyState';
import { Modal } from '../../../../components/Modals/Modal';
import ApplyForJobModal from '../../../../components/Modals/ModalsActions/ApplyForJobModal';
import ReportJobModal from '../../../../components/Modals/ModalsActions/ReportJobModal';
import ModalTriggerContainer from '../../../../components/Modals/ModalTriggerContainer';
import { ModalProvider } from '../../../../context/ModalContext';
import useGetSingleLocumJob from '../../../../hooks/dashboard/jobs/useGetSingleLocumJob';
import useQueryString from '../../../../hooks/ui-control/useQueryString';
import { useAppSelector, useAppDisPatch } from '../../../../redux/store';
import { locumSingleJobsActions } from '../../../../redux/slices/locumSingleJobSlice';
import JobInfoBody from './JobInfoBody';
import JobInfoHead from './JobInfoHead';
import GuestPromptModal from '../../../../components/Modals/ModalsActions/GuestPromptModal';
import JobInfoSkeleton from '../../../../components/Skeletons/JobInfoSkeleton';
import { useJobSearch } from '../../../../context/JobSearchContext';

export interface SelectedJobsProps {
  id: string;
  jobCreatorId: string;
  imgSrc: string;
  title: string;
  jobDescription: any;
  institutionName: string;
  yearsOfExperience: string;
  location: string;
  pay: number;
  payInterval: string;
  jobStartDate: string;
  jobEndDate: string;
}

interface JobInfoProps {
  showMobileInfo?: boolean;
  guest?: boolean;
}

const JobInfo: React.FC<JobInfoProps> = ({
  showMobileInfo,
  guest,
}) => {
  // 1) Read the URL param, but never set it here
  const [queryParams] = useQueryString();
  const jobId = queryParams.get('jobId');

  // 2) Fetching slice + hook
  const { getSingleLocumJob, loading } = useGetSingleLocumJob();
  const { jobData } = useAppSelector((s) => s.locumSingleJobs);
  const dispatch = useAppDisPatch();

  // 3) External loading (e.g. search context)
  const { loading: contexting } = useJobSearch();

  // 4) One “ready” flag for skeleton
  const [isReady, setIsReady] = useState(false);

  // ------------------------------------------------

  useEffect(() => {
    if (!jobId) return;

    // Clear and fetch
    dispatch(locumSingleJobsActions.clearSingleLocumJob());
    getSingleLocumJob(jobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // 2. Define a single async checker that covers all your conditions:
  async function allReady(): Promise<boolean> {
    const dataReady =
      !loading && !contexting && jobData != null && jobData.id === jobId;
    if (!dataReady) {
      return false;
    }
    return true;
  }

  useEffect(() => {
    setIsReady(false);

    allReady().then((ok) => {
      setIsReady(ok);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, contexting, jobData, jobId]);

  if (!jobId) {
    return (
      <EmptyState className="no-small-screen">
        Click on job to preview
      </EmptyState>
    );
  }

  if (!isReady) {
    return (
      <StickyWrapper className={showMobileInfo ? 'showMobileInfo' : ''}>
        <Container>
          <JobInfoSkeleton />
        </Container>
      </StickyWrapper>
    );
  }

  return (
    <StickyWrapper className={showMobileInfo ? 'showMobileInfo' : ''}>
      <Container>
        <JobInfoHead
          imgSrc={'/static/gif/happyAnimal.gif'}
          title={jobData?.title ?? ''}
          institutionName={jobData?.institutionName ?? ''}
          yearsOfExperience={`${jobData?.yearsOfExperience ?? ''} years+`}
          location={jobData?.location ?? ''}
          pay={jobData?.pay ?? 0}
          payInterval={jobData?.payInterval ?? ''}
          jobStartDate={jobData?.jobStartDate ?? ''}
          jobEndDate={jobData?.jobEndDate ?? ''}
          jobId={jobData?.id ?? ''}
          guest={guest}
        />

        <JobInfoBody jobDescription={jobData?.jobDescription} />

        <ButtonArea>
          <ModalProvider>
            <ModalTriggerContainer>
              <SecondaryButton
                className="pl-pr-4 fw600"
                disabled={loading || contexting}
              >
                {'Apply'}
              </SecondaryButton>
            </ModalTriggerContainer>

            <Modal>
              {guest ? (
                <GuestPromptModal />
              ) : (
                <ApplyForJobModal
                  jobId={jobId}
                  jobCreatorId={jobData?.jobCreatorId}
                />
              )}
            </Modal>
          </ModalProvider>

          <ModalProvider>
            <ModalTriggerContainer>
              <PrimaryButton className="danger fw600">Report</PrimaryButton>
            </ModalTriggerContainer>

            <Modal>
              {guest ? (
                <GuestPromptModal />
              ) : (
                <ReportJobModal
                  title={jobData?.title ?? ''}
                  description={jobData?.institutionName ?? ''}
                  jobId={jobId}
                />
              )}
            </Modal>
          </ModalProvider>
        </ButtonArea>
      </Container>
    </StickyWrapper>
  );
};

export default JobInfo;

const StickyWrapper = styled.div`
  display: flex;
  position: sticky;
  top: 160px;
  height: 80vh;
  overflow: auto;

  @media (max-width: 884px) {
    display: none;
    &.showMobileInfo {
      display: grid;
    }
  }
`;

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;
  padding: 32px;
  display: grid;
  gap: 16px;
  height: fit-content;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.white};

  @media (max-width: 540px) {
    padding: 1rem;
    border-radius: 0;
    border: none;
  }
`;

const ButtonArea = styled.div`
  display: flex;
  gap: 2rem;
  height: fit-content;
`;
